import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, KeyRound, ShieldCheck, AlertTriangle } from "lucide-react";
import { formatApiError } from "../contexts/AuthContext";

export default function ChangePassword() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isForced = params.get("forced") === "1" || user?.must_change_password;

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPwd.length < 8) { setError("Le nouveau mot de passe doit faire au moins 8 caractères."); return; }
    if (newPwd !== confirmPwd) { setError("La confirmation ne correspond pas."); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd, new_password: newPwd,
      });
      if (refreshUser) await refreshUser();
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {isForced && (
        <div
          data-testid="forced-change-banner"
          className="bg-accent/10 border-l-4 border-accent rounded-md p-4 mb-8 flex items-start gap-3"
        >
          <AlertTriangle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-ink mb-1">Action requise</p>
            <p className="text-sm text-ink/70 leading-relaxed">
              Vous vous êtes connecté·e avec un <strong>mot de passe provisoire</strong>. Veuillez en définir un nouveau dès maintenant pour sécuriser votre compte.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <KeyRound className="h-5 w-5 text-accent" />
        <p className="text-label text-muted-foreground">Sécurité</p>
      </div>
      <h1 className="font-heading font-light tracking-editorial text-4xl text-ink mb-3">
        Changer mon <em className="font-display text-accent">mot de passe</em>
      </h1>
      <p className="text-muted-foreground mb-10 max-w-xl text-sm leading-relaxed">
        Choisissez un mot de passe robuste (8 caractères minimum). Une fois validé, votre session reste active.
      </p>

      <form onSubmit={submit} className="bg-white border border-ink/10 rounded-md p-6 lg:p-8 space-y-5">
        <div>
          <Label htmlFor="current-pwd">{isForced ? "Mot de passe provisoire reçu par email" : "Mot de passe actuel"}</Label>
          <Input
            id="current-pwd"
            type="password"
            required
            autoComplete="current-password"
            autoFocus
            value={currentPwd}
            onChange={(e) => setCurrentPwd(e.target.value)}
            data-testid="change-pwd-current"
          />
        </div>
        <div>
          <Label htmlFor="new-pwd">Nouveau mot de passe</Label>
          <Input
            id="new-pwd"
            type="password"
            required
            autoComplete="new-password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            data-testid="change-pwd-new"
          />
          <p className="text-xs text-muted-foreground mt-1">8 caractères minimum.</p>
        </div>
        <div>
          <Label htmlFor="confirm-pwd">Confirmer le nouveau mot de passe</Label>
          <Input
            id="confirm-pwd"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            data-testid="change-pwd-confirm"
          />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="change-pwd-error">{error}</p>}
        <div className="flex justify-end pt-2 border-t border-ink/10">
          <Button
            type="submit"
            disabled={loading}
            data-testid="change-pwd-submit"
            className="bg-ink hover:bg-ink/85 text-cream rounded-none px-6"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Mettre à jour</span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
